import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import Modal from '../components/common/Modal';
import Pagination from '../components/common/Pagination';
import BulkUpload from '../components/common/BulkUpload';
import { SkeletonTable } from '../components/common/Skeleton';
import { useAuth } from '../context/AuthContext';
import { getAvatarColors } from '../utils/avatars';
import { useDebounce } from '../hooks/useDebounce';
import { docTypeLabel } from '../utils/documentUtils';
import { INDIAN_STATES, COUNTRIES, MAJOR_CITIES } from '../config/geoData';


const TYPES = ['doctor', 'hospital', 'clinic', 'pharmacy', 'distributor'];
const POTENTIALS = ['high', 'medium', 'low'];
const typeColors = { doctor: 'primary', hospital: 'info', clinic: 'success', pharmacy: 'warning', distributor: 'neutral' };
const ID_TYPES = ['GSTN', 'TIN', 'GST/HST', 'OTHERS'];
const PREDEFINED_ID_TYPES = ['GSTN', 'TIN', 'GST/HST'];

const defaultForm = {
  name: '', type: 'doctor', specialty: '', qualification: '', email: '', phone: '',
  company_id_type: '', company_id_value: '',
  address: '', city: '', state: '', zip_code: '', country: 'India',
  billing_same_as_primary: true, billing_address: '', billing_city: '', billing_state: '', billing_zip_code: '', billing_country: '',
  shipping_same_as_primary: true, shipping_address: '', shipping_city: '', shipping_state: '', shipping_zip_code: '', shipping_country: '',
  assigned_to: '', potential: 'medium', lifecycle_stage: 'prospect', notes: '', territory_id: '',
  license_number: '', license_expiry_date: '', credit_limit: '',
};

export default function Customers() {
  const { isAdmin, isManager } = useAuth();
  const [data, setData] = useState({ data: [], total: 0, page: 1, limit: 15 });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [lifecycleFilter, setLifecycleFilter] = useState('');
  const [territoryFilter, setTerritoryFilter] = useState('');
  const [page, setPage] = useState(1);
  const [modal, setModal] = useState({ open: false, mode: 'add', item: null });
  const [viewModal, setViewModal] = useState({ open: false, customer: null, visits: [], orders: [], activities: [], competitorMentions: [] });
  const [form, setForm] = useState(defaultForm);
  const [users, setUsers] = useState([]);
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [error, setError] = useState('');
  const [dupWarning, setDupWarning] = useState({ open: false, matches: [] });
  const [bulkOpen, setBulkOpen] = useState(false);
  const [companyIdSel, setCompanyIdSel] = useState('');
  const [createDocForm, setCreateDocForm] = useState({ doc_type: 'nda', doc_description: '', file: null });
  const [docs, setDocs] = useState([]);
  const [docForm, setDocForm] = useState({ doc_type: 'nda', doc_description: '', file: null });
  const [docUploading, setDocUploading] = useState(false);
  const [docError, setDocError] = useState('');
  const [territories, setTerritories] = useState([]);
  const [extraAddresses, setExtraAddresses] = useState([]);
  const [addrForm, setAddrForm] = useState({ label: '', address_type: 'other', address: '', city: '', state: '', zip_code: '', country: 'India' });
  const [addrEditId, setAddrEditId] = useState(null);
  const [addrFormOpen, setAddrFormOpen] = useState(false);
  const [addrSaving, setAddrSaving] = useState(false);
  const [addrModal, setAddrModal] = useState({ open: false, entityId: null, entityName: '' });

  const debouncedSearch = useDebounce(search, 300);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 15, search: debouncedSearch || undefined, type: typeFilter || undefined, lifecycle_stage: lifecycleFilter || undefined, territory_id: territoryFilter || undefined };
      const { data: res } = await api.get('/customers', { params });
      setData(res);
    } finally { setLoading(false); }
  }, [page, debouncedSearch, typeFilter, lifecycleFilter, territoryFilter]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get('/users', { params: { is_active: true } }).then(r => setUsers(r.data.data || [])).catch(() => { }); }, []);
  useEffect(() => { api.get('/territories').then(r => setTerritories(r.data.data || r.data || [])).catch(() => { }); }, []);

  const openAdd = () => {
    setForm(defaultForm);
    setCompanyIdSel('');
    setCreateDocForm({ doc_type: 'nda', doc_description: '', file: null });
    setError('');
    setModal({ open: true, mode: 'add', item: null });
  };
  const openEdit = (c) => {
    setForm({
      ...c,
      assigned_to: c.assigned_to || '',
      billing_same_as_primary: c.billing_same_as_primary !== 0,
      shipping_same_as_primary: c.shipping_same_as_primary !== 0,
      billing_address: c.billing_address || '',
      billing_city: c.billing_city || '',
      billing_state: c.billing_state || '',
      billing_zip_code: c.billing_zip_code || '',
      billing_country: c.billing_country || '',
      shipping_address: c.shipping_address || '',
      shipping_city: c.shipping_city || '',
      shipping_state: c.shipping_state || '',
      shipping_zip_code: c.shipping_zip_code || '',
      shipping_country: c.shipping_country || '',
      license_number: c.license_number || '',
      license_expiry_date: c.license_expiry_date ? c.license_expiry_date.slice(0, 10) : '',
      credit_limit: c.credit_limit != null ? c.credit_limit : '',
      territory_id: c.territory_id || '',
    });
    setCompanyIdSel(PREDEFINED_ID_TYPES.includes(c.company_id_type) ? c.company_id_type : (c.company_id_type ? 'OTHERS' : ''));
    setCreateDocForm({ doc_type: 'nda', doc_description: '', file: null });
    setError('');
    setModal({ open: true, mode: 'edit', item: c });
  };

  const openView = async (c) => {
    setExtraAddresses([]);
    setAddrFormOpen(false);
    setAddrEditId(null);
    setAddrForm({ label: '', address_type: 'other', address: '', city: '', state: '', zip_code: '', country: 'India' });
    const [v, o, docsRes, activitiesRes, competitorsRes, addrRes] = await Promise.all([
      api.get(`/customers/${c.id}/visits`),
      api.get(`/customers/${c.id}/orders`),
      api.get(`/customers/${c.id}/documents`),
      api.get('/activities', { params: { customer_id: c.id } }).catch(() => ({ data: [] })),
      api.get('/competitors/mentions', { params: { customer_id: c.id } }).catch(() => ({ data: [] })),
      api.get('/addresses', { params: { entity_type: 'customer', entity_id: c.id } }).catch(() => ({ data: { data: [] } })),
    ]);
    const customerMentions = Array.isArray(competitorsRes.data) ? competitorsRes.data : (competitorsRes.data?.data || []);
    setViewModal({
      open: true, customer: c,
      visits: v.data.data,
      orders: o.data.data,
      activities: Array.isArray(activitiesRes.data) ? activitiesRes.data : [],
      competitorMentions: customerMentions,
    });
    setDocs(docsRes.data.data || []);
    setExtraAddresses(addrRes.data.data || []);
    setDocForm({ doc_type: 'nda', doc_description: '', file: null });
    setDocError('');
  };

  const loadDocs = async (id) => {
    const { data } = await api.get(`/customers/${id}/documents`);
    setDocs(data.data || []);
  };

  const loadExtraAddresses = async (id) => {
    const { data } = await api.get('/addresses', { params: { entity_type: 'customer', entity_id: id } });
    setExtraAddresses(data.data || []);
  };

  const openAddrModal = async (c) => {
    setAddrFormOpen(false);
    setAddrEditId(null);
    setAddrForm({ label: '', address_type: 'other', address: '', city: '', state: '', zip_code: '', country: 'India' });
    setAddrModal({ open: true, entityId: c.id, entityName: c.name });
    const { data } = await api.get('/addresses', { params: { entity_type: 'customer', entity_id: c.id } });
    setExtraAddresses(data.data || []);
  };

  const currentAddrEntityId = addrModal.open ? addrModal.entityId : viewModal.customer?.id;

  const handleAddrSave = async () => {
    if (!addrForm.label.trim()) return;
    setAddrSaving(true);
    try {
      if (addrEditId) {
        await api.put(`/addresses/${addrEditId}`, addrForm);
      } else {
        await api.post('/addresses', { ...addrForm, entity_type: 'customer', entity_id: currentAddrEntityId });
      }
      await loadExtraAddresses(currentAddrEntityId);
      setAddrFormOpen(false);
      setAddrEditId(null);
      setAddrForm({ label: '', address_type: 'other', address: '', city: '', state: '', zip_code: '', country: 'India' });
    } catch { /* ignore */ } finally { setAddrSaving(false); }
  };

  const handleAddrDelete = async (id) => {
    await api.delete(`/addresses/${id}`);
    await loadExtraAddresses(currentAddrEntityId);
  };

  const startAddrEdit = (addr) => {
    setAddrEditId(addr.id);
    setAddrForm({ label: addr.label, address_type: addr.address_type, address: addr.address || '', city: addr.city || '', state: addr.state || '', zip_code: addr.zip_code || '', country: addr.country || 'India' });
    setAddrFormOpen(true);
  };

  const handleDocUpload = async () => {
    if (!docForm.file) { setDocError('Please select a file.'); return; }
    if (docForm.doc_type === 'others' && !docForm.doc_description.trim()) { setDocError('Please describe the document type.'); return; }
    setDocUploading(true); setDocError('');
    try {
      const fd = new FormData();
      fd.append('file', docForm.file);
      fd.append('doc_type', docForm.doc_type);
      if (docForm.doc_description) fd.append('doc_description', docForm.doc_description);
      await api.post(`/customers/${viewModal.customer.id}/documents`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setDocForm({ doc_type: 'nda', doc_description: '', file: null });
      await loadDocs(viewModal.customer.id);
    } catch (err) { setDocError(err.response?.data?.message || 'Upload failed.'); }
    finally { setDocUploading(false); }
  };

  const handleDocDelete = async (docId) => {
    if (!window.confirm('Delete this document?')) return;
    await api.delete(`/customers/${viewModal.customer.id}/documents/${docId}`);
    await loadDocs(viewModal.customer.id);
  };

  const handleDocDownload = (docId, name) => {
    const token = localStorage.getItem('token');
    fetch(`${process.env.REACT_APP_API_URL || '/api'}/customers/${viewModal.customer.id}/documents/${docId}/download`,
      { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.blob()).then(blob => { const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = name; a.click(); });
  };

  const docTypeIcon = (filename = '') => {
    const ext = filename.split('.').pop().toLowerCase();
    if (ext === 'pdf') return '📄';
    if (['doc', 'docx'].includes(ext)) return '📝';
    if (['xls', 'xlsx'].includes(ext)) return '📊';
    if (['png', 'jpg', 'jpeg', 'webp'].includes(ext)) return '🖼️';
    return '📎';
  };

  const handleSave = async (skipDupCheck = false) => {
    if (!form.name || !form.type) { setError('Name and type are required.'); return; }
    if (modal.mode === 'add' && !skipDupCheck) {
      try {
        const { data: res } = await api.get('/customers', { params: { search: form.name.trim(), limit: 5 } });
        const matches = (res.data || []).filter(c =>
          c.name.toLowerCase().includes(form.name.trim().toLowerCase()) ||
          form.name.trim().toLowerCase().includes(c.name.toLowerCase())
        );
        if (matches.length > 0) {
          setDupWarning({ open: true, matches });
          return;
        }
      } catch { /* proceed if check fails */ }
    }
    setSaving(true); setError('');
    try {
      let customerId;
      if (modal.mode === 'add') {
        const res = await api.post('/customers', form);
        customerId = res.data.data.id;
      } else {
        await api.put(`/customers/${modal.item.id}`, form);
        customerId = modal.item.id;
      }
      if (createDocForm.file) {
        try {
          const fd = new FormData();
          fd.append('file', createDocForm.file);
          fd.append('doc_type', createDocForm.doc_type || 'others');
          if (createDocForm.doc_description) fd.append('doc_description', createDocForm.doc_description);
          await api.post(`/customers/${customerId}/documents`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        } catch (docErr) {
          setError(`Record saved but document upload failed: ${docErr.response?.data?.message || 'Upload error'}`);
          setSaving(false);
          load();
          return;
        }
      }
      setModal({ open: false, mode: 'add', item: null });
      load();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save customer.');
    } finally { setSaving(false); }
  };

  const handleDelete = async () => {
    await api.delete(`/customers/${deleteConfirm.id}`);
    setDeleteConfirm(null); load();
  };

  const handleUnfreeze = async (id) => {
    try { await api.patch(`/customers/${id}/unfreeze`); load(); setModal({ open: false }); }
    catch (err) { setError(err.response?.data?.message || 'Failed to unfreeze.'); }
  };

  const f = (k, v) => setForm(p => ({ ...p, [k]: v }));

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h1 className="page-title">Customers</h1>
          <p className="page-subtitle">Manage doctors, hospitals, pharmacies and distributors</p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-secondary" onClick={() => setBulkOpen(true)}>
            <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" /><polyline points="17 8 12 3 7 8" /><line x1="12" y1="3" x2="12" y2="15" /></svg>
            Bulk Upload
          </button>
          <button className="btn btn-primary" onClick={openAdd}>
            <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>
            Add Customer
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-body" style={{ padding: '14px 16px' }}>
          <div className="search-bar">
            <div className="search-input-wrap">
              <span className="search-icon"><svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></svg></span>
              <input className="form-control" placeholder="Search name, email, phone, city…"
                value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} />
            </div>
            <select className="form-control form-select" style={{ width: 160 }} value={typeFilter} onChange={e => { setTypeFilter(e.target.value); setPage(1); }}>
              <option value="">All Types</option>
              {TYPES.map(t => <option key={t} value={t} style={{ textTransform: 'capitalize' }}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>)}
            </select>
            <select className="form-control form-select" style={{ width: 160 }} value={lifecycleFilter} onChange={e => { setLifecycleFilter(e.target.value); setPage(1); }}>
              <option value="">All Stages</option>
              {['prospect','lead','qualified','opportunity','customer','advocate','churned'].map(s => (
                <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>
              ))}
            </select>
            <select className="form-control form-select" style={{ width: 180 }} value={territoryFilter} onChange={e => { setTerritoryFilter(e.target.value); setPage(1); }}>
              <option value="">All Territories</option>
              {territories.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
            {(search || typeFilter || lifecycleFilter || territoryFilter) && (
              <button className="btn btn-ghost btn-sm" onClick={() => { setSearch(''); setTypeFilter(''); setLifecycleFilter(''); setTerritoryFilter(''); setPage(1); }}>Clear</button>
            )}
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="card">
        <div className="table-container">
          <table className="table">
            <thead><tr><th>Name</th><th>Type</th><th>Specialty</th><th>Phone</th><th>City</th><th>Territory</th><th>Potential</th><th>Credit</th><th>Last Visit</th><th>Assigned To</th><th>Actions</th></tr></thead>
            <tbody>
              {loading && <tr><td colSpan={11} style={{ padding: 0 }}><SkeletonTable rows={8} cols={11} /></td></tr>}
              {!loading && data.data.length === 0 && <tr><td colSpan={11}><div className="empty-state"><p>No customers found. Add your first customer.</p></div></td></tr>}
              {!loading && data.data.map(c => (
                <tr key={c.id}>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      {(() => {
                        const [fg] = getAvatarColors(c.name);
                        const initials = c.name.split(' ').slice(0, 2).map(w => w[0]?.toUpperCase() || '').join('');
                        return (
                          <div style={{ width: 34, height: 34, borderRadius: '50%', flexShrink: 0, background: `linear-gradient(135deg, ${fg}22, ${fg}44)`, border: `1.5px solid ${fg}44`, color: fg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: '0.76rem' }}>
                            {initials}
                          </div>
                        );
                      })()}
                      <div>
                        <div style={{ fontWeight: 600, color: c.is_active === 0 ? 'var(--text-muted)' : undefined }}>{c.name}{c.is_active === 0 && <span className="badge badge-neutral" style={{ fontSize: '0.65rem', marginLeft: 6 }}>Frozen</span>}</div>
                        {c.email && <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{c.email}</div>}
                      </div>
                    </div>
                  </td>
                  <td><span className={`badge badge-${typeColors[c.type] || 'neutral'}`} style={{ textTransform: 'capitalize' }}>{c.type}</span></td>
                  <td style={{ color: 'var(--text-secondary)' }}>{c.specialty || '—'}</td>
                  <td>{c.phone || '—'}</td>
                  <td>{c.city || '—'}</td>
                  <td style={{ color: 'var(--text-secondary)', fontSize: '0.83rem' }}>{c.territory_name || '—'}</td>
                  <td><span className={`badge badge-${c.potential === 'high' ? 'success' : c.potential === 'low' ? 'error' : 'warning'}`} style={{ textTransform: 'capitalize' }}>{c.potential}</span></td>
                  <td style={{ fontSize: '0.78rem' }}>
                    {c.credit_limit != null ? (
                      <div>
                        <div style={{ color: 'var(--text-secondary)' }}>Limit: &#8377;{Number(c.credit_limit).toLocaleString()}</div>
                        {c.outstanding > c.credit_limit && <span className="badge badge-danger" style={{ fontSize: '0.65rem' }}>OVERLIMIT</span>}
                      </div>
                    ) : '—'}
                  </td>
                  <td style={{ fontSize: '0.82rem' }}>
                    {(() => {
                      if (!c.last_visit_date) {
                        return <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}><span style={{ width: 8, height: 8, borderRadius: '50%', background: '#ef4444', display: 'inline-block', flexShrink: 0 }} />Never</span>;
                      }
                      const d = new Date(c.last_visit_date);
                      const daysDiff = Math.floor((Date.now() - d.getTime()) / 86400000);
                      const dot = daysDiff <= 30 ? '#22c55e' : daysDiff <= 90 ? '#f59e0b' : '#ef4444';
                      const label = d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
                      return <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}><span style={{ width: 8, height: 8, borderRadius: '50%', background: dot, display: 'inline-block', flexShrink: 0 }} />{label}</span>;
                    })()}
                  </td>
                  <td style={{ color: 'var(--text-secondary)', fontSize: '0.83rem' }}>{c.assigned_to_name || '—'}</td>
                  <td>
                    <div className="table-actions">
                      <button className="btn btn-ghost btn-icon" data-tooltip="View Details" onClick={() => openView(c)}>
                        <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" /></svg>
                      </button>
                      {(isAdmin || isManager) && (
                        <button className="btn btn-ghost btn-icon" data-tooltip="Manage Addresses" onClick={() => openAddrModal(c)}>
                          <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z" /><circle cx="12" cy="10" r="3" /></svg>
                        </button>
                      )}
                      <button className="btn btn-ghost btn-icon" data-tooltip="Edit" onClick={() => openEdit(c)}>
                        <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" /><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" /></svg>
                      </button>
                      {isManager && c.is_active !== 0 && (
                        <button className="btn btn-ghost btn-icon" data-tooltip="Freeze" style={{ color: 'var(--error)' }} onClick={() => setDeleteConfirm(c)}>
                          <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><rect x="3" y="11" width="18" height="11" rx="2" /><path d="M7 11V7a5 5 0 0110 0v4" /></svg>
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <Pagination page={data.page} total={data.total} limit={data.limit} onPageChange={setPage} />
      </div>

      {/* Duplicate Warning Modal */}
      {dupWarning.open && (
        <Modal open={dupWarning.open} onClose={() => setDupWarning({ open: false, matches: [] })}
          title="Possible Duplicate Detected"
          footer={<>
            <button className="btn btn-secondary" onClick={() => setDupWarning({ open: false, matches: [] })}>Cancel</button>
            <button className="btn btn-primary" onClick={() => { setDupWarning({ open: false, matches: [] }); handleSave(true); }}>Save Anyway</button>
          </>}
        >
          <div style={{ marginBottom: 12, color: '#92400e', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 6, padding: '10px 14px', fontSize: 14 }}>
            The following customer(s) with a similar name already exist. Are you sure you want to create a new record?
          </div>
          <ul style={{ margin: 0, paddingLeft: 18, fontSize: 14, color: '#374151' }}>
            {dupWarning.matches.map(m => (
              <li key={m.id} style={{ marginBottom: 4 }}>
                <b>{m.name}</b> &nbsp;·&nbsp; {m.type} &nbsp;·&nbsp; {m.city || ''}
              </li>
            ))}
          </ul>
        </Modal>
      )}

      {/* Add/Edit Modal */}
      <Modal open={modal.open} onClose={() => setModal({ open: false })} title={modal.mode === 'add' ? 'Add Customer' : 'Edit Customer'}
        size="lg"
        footer={<>
          <button className="btn btn-secondary" onClick={() => setModal({ open: false })}>Cancel</button>
          {modal.item?.is_active === 0
            ? <button className="btn btn-primary" style={{ background: '#dc2626' }} onClick={() => handleUnfreeze(modal.item.id)}>Unfreeze</button>
            : <button className="btn btn-primary" onClick={handleSave} disabled={saving}>{saving ? <><div className="spinner" style={{ width: 14, height: 14 }} /> Saving…</> : 'Save Customer'}</button>
          }
        </>}
      >
        {error && <div className="alert alert-error">{error}</div>}
        {modal.item?.is_active === 0 && (
          <div style={{ marginBottom: 16, padding: '12px 16px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8 }}>
            <div style={{ fontWeight: 700, color: '#dc2626', fontSize: '0.875rem' }}>⛔ Record Frozen</div>
            <div style={{ color: '#991b1b', fontSize: '0.8rem', marginTop: 2 }}>This record is frozen and cannot be edited. Unfreeze to make changes.</div>
          </div>
        )}
        <div className="form-row">
          <div className="form-group">
            <label className="form-label">Name *</label>
            <input className="form-control" value={form.name} onChange={e => f('name', e.target.value)} placeholder="Dr. John Smith" disabled={modal.item?.is_active === 0} />
          </div>
          <div className="form-group">
            <label className="form-label">Type *</label>
            <select className="form-control form-select" value={form.type} onChange={e => f('type', e.target.value)} disabled={modal.item?.is_active === 0}>
              {TYPES.map(t => <option key={t} value={t} style={{ textTransform: 'capitalize' }}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>)}
            </select>
          </div>
        </div>
        <div className="form-row">
          <div className="form-group"><label className="form-label">Specialty</label><input className="form-control" value={form.specialty} onChange={e => f('specialty', e.target.value)} placeholder="Cardiology" disabled={modal.item?.is_active === 0} /></div>
          <div className="form-group"><label className="form-label">Qualification</label><input className="form-control" value={form.qualification} onChange={e => f('qualification', e.target.value)} placeholder="MBBS, MD" disabled={modal.item?.is_active === 0} /></div>
        </div>
        <div className="form-row">
          <div className="form-group"><label className="form-label">Email</label><input className="form-control" type="email" value={form.email} onChange={e => f('email', e.target.value)} disabled={modal.item?.is_active === 0} /></div>
          <div className="form-group"><label className="form-label">Phone</label><input className="form-control" value={form.phone} onChange={e => f('phone', e.target.value)} disabled={modal.item?.is_active === 0} /></div>
        </div>
        <div className="form-row">
          <div className="form-group">
            <label className="form-label">Company ID Type</label>
            <select className="form-control form-select" value={companyIdSel} disabled={modal.item?.is_active === 0} onChange={e => {
              setCompanyIdSel(e.target.value);
              if (e.target.value !== 'OTHERS') f('company_id_type', e.target.value);
              else f('company_id_type', '');
            }}>
              <option value="">-- Select --</option>
              {ID_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">{companyIdSel === 'OTHERS' ? 'Custom ID Type *' : 'Company ID Value'}</label>
            {companyIdSel === 'OTHERS' ? (
              <div style={{ display: 'flex', gap: 8 }}>
                <input className="form-control" value={form.company_id_type} onChange={e => f('company_id_type', e.target.value)} placeholder="e.g. PAN, VAT, CIN…" style={{ flex: 1 }} disabled={modal.item?.is_active === 0} />
                <input className="form-control" value={form.company_id_value} onChange={e => f('company_id_value', e.target.value)} placeholder="ID number" style={{ flex: 1 }} disabled={modal.item?.is_active === 0} />
              </div>
            ) : (
              <input className="form-control" value={form.company_id_value} onChange={e => f('company_id_value', e.target.value)} placeholder="ID number / value" disabled={modal.item?.is_active === 0} />
            )}
          </div>
        </div>
        {/* Primary Address */}
        <div style={{ borderTop: '1px solid var(--border)', paddingTop: 12, marginTop: 4 }}>
          <div style={{ fontWeight: 700, fontSize: '0.82rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: 8 }}>Primary Address</div>
          <div className="form-group"><label className="form-label">Address</label><textarea className="form-control" rows={2} value={form.address} onChange={e => f('address', e.target.value)} disabled={modal.item?.is_active === 0} /></div>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">City</label>
              {form.country === 'India' && form.state && MAJOR_CITIES[form.state] ? (
                <select className="form-control form-select" value={form.city} onChange={e => f('city', e.target.value)} disabled={modal.item?.is_active === 0}>
                  <option value="">-- Select City --</option>
                  {MAJOR_CITIES[form.state].map(city => <option key={city} value={city}>{city}</option>)}
                </select>
              ) : (
                <input className="form-control" value={form.city} onChange={e => f('city', e.target.value)} disabled={modal.item?.is_active === 0} placeholder="Enter city" />
              )}
            </div>
            <div className="form-group"><label className="form-label">Zip Code</label><input className="form-control" value={form.zip_code} onChange={e => f('zip_code', e.target.value)} disabled={modal.item?.is_active === 0} /></div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Country</label>
              <select className="form-control form-select" value={form.country} 
                onChange={e => {
                  const val = e.target.value;
                  f('country', val);
                  if (val !== 'India') f('state', '');
                }} 
                disabled={modal.item?.is_active === 0}>
                {COUNTRIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">State</label>
              {form.country === 'India' ? (
                <select className="form-control form-select" value={form.state} onChange={e => f('state', e.target.value)} disabled={modal.item?.is_active === 0}>
                  <option value="">-- Select State --</option>
                  {INDIAN_STATES.map(s => <option key={s.code} value={s.name}>{s.name}</option>)}
                </select>
              ) : (
                <input className="form-control" value={form.state} onChange={e => f('state', e.target.value)} disabled={modal.item?.is_active === 0} placeholder="Enter state" />
              )}
            </div>
          </div>

        </div>
        {/* Billing Address */}
        <div style={{ borderTop: '1px solid var(--border)', paddingTop: 12, marginTop: 4 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
            <div style={{ fontWeight: 700, fontSize: '0.82rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.4px' }}>Billing Address</div>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: '0.82rem', color: 'var(--text-secondary)', fontWeight: 500 }}>
              <input type="checkbox" checked={!!form.billing_same_as_primary} disabled={modal.item?.is_active === 0}
                onChange={e => f('billing_same_as_primary', e.target.checked)} />
              Same as Primary
            </label>
          </div>
          {form.billing_same_as_primary
            ? <div style={{ padding: '8px 12px', background: 'var(--bg-secondary)', borderRadius: 6, fontSize: '0.82rem', color: 'var(--text-muted)' }}>Uses primary address</div>
            : <>
              <div className="form-group"><label className="form-label">Billing Address</label><textarea className="form-control" rows={2} value={form.billing_address} onChange={e => f('billing_address', e.target.value)} disabled={modal.item?.is_active === 0} /></div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">City</label>
                  {form.billing_country === 'India' && form.billing_state && MAJOR_CITIES[form.billing_state] ? (
                    <select className="form-control form-select" value={form.billing_city} onChange={e => f('billing_city', e.target.value)} disabled={modal.item?.is_active === 0}>
                      <option value="">-- Select City --</option>
                      {MAJOR_CITIES[form.billing_state].map(city => <option key={city} value={city}>{city}</option>)}
                    </select>
                  ) : (
                    <input className="form-control" value={form.billing_city} onChange={e => f('billing_city', e.target.value)} disabled={modal.item?.is_active === 0} placeholder="Enter city" />
                  )}
                </div>
                <div className="form-group"><label className="form-label">Zip Code</label><input className="form-control" value={form.billing_zip_code} onChange={e => f('billing_zip_code', e.target.value)} disabled={modal.item?.is_active === 0} /></div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Country</label>
                  <select className="form-control form-select" value={form.billing_country} 
                    onChange={e => {
                      const val = e.target.value;
                      f('billing_country', val);
                      if (val !== 'India') f('billing_state', '');
                    }} 
                    disabled={modal.item?.is_active === 0}>
                    <option value="">-- Select Country --</option>
                    {COUNTRIES.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">State</label>
                  {form.billing_country === 'India' ? (
                    <select className="form-control form-select" value={form.billing_state} onChange={e => f('billing_state', e.target.value)} disabled={modal.item?.is_active === 0}>
                      <option value="">-- Select State --</option>
                      {INDIAN_STATES.map(s => <option key={s.code} value={s.name}>{s.name}</option>)}
                    </select>
                  ) : (
                    <input className="form-control" value={form.billing_state} onChange={e => f('billing_state', e.target.value)} disabled={modal.item?.is_active === 0} placeholder="Enter state" />
                  )}
                </div>
              </div>

            </>
          }
        </div>
        {/* Shipping Address */}
        <div style={{ borderTop: '1px solid var(--border)', paddingTop: 12, marginTop: 4 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
            <div style={{ fontWeight: 700, fontSize: '0.82rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.4px' }}>Shipping Address</div>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: '0.82rem', color: 'var(--text-secondary)', fontWeight: 500 }}>
              <input type="checkbox" checked={!!form.shipping_same_as_primary} disabled={modal.item?.is_active === 0}
                onChange={e => f('shipping_same_as_primary', e.target.checked)} />
              Same as Primary
            </label>
          </div>
          {form.shipping_same_as_primary
            ? <div style={{ padding: '8px 12px', background: 'var(--bg-secondary)', borderRadius: 6, fontSize: '0.82rem', color: 'var(--text-muted)' }}>Uses primary address</div>
            : <>
              <div className="form-group"><label className="form-label">Shipping Address</label><textarea className="form-control" rows={2} value={form.shipping_address} onChange={e => f('shipping_address', e.target.value)} disabled={modal.item?.is_active === 0} /></div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">City</label>
                  {form.shipping_country === 'India' && form.shipping_state && MAJOR_CITIES[form.shipping_state] ? (
                    <select className="form-control form-select" value={form.shipping_city} onChange={e => f('shipping_city', e.target.value)} disabled={modal.item?.is_active === 0}>
                      <option value="">-- Select City --</option>
                      {MAJOR_CITIES[form.shipping_state].map(city => <option key={city} value={city}>{city}</option>)}
                    </select>
                  ) : (
                    <input className="form-control" value={form.shipping_city} onChange={e => f('shipping_city', e.target.value)} disabled={modal.item?.is_active === 0} placeholder="Enter city" />
                  )}
                </div>
                <div className="form-group"><label className="form-label">Zip Code</label><input className="form-control" value={form.shipping_zip_code} onChange={e => f('shipping_zip_code', e.target.value)} disabled={modal.item?.is_active === 0} /></div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Country</label>
                  <select className="form-control form-select" value={form.shipping_country} 
                    onChange={e => {
                      const val = e.target.value;
                      f('shipping_country', val);
                      if (val !== 'India') f('shipping_state', '');
                    }} 
                    disabled={modal.item?.is_active === 0}>
                    <option value="">-- Select Country --</option>
                    {COUNTRIES.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">State</label>
                  {form.shipping_country === 'India' ? (
                    <select className="form-control form-select" value={form.shipping_state} onChange={e => f('shipping_state', e.target.value)} disabled={modal.item?.is_active === 0}>
                      <option value="">-- Select State --</option>
                      {INDIAN_STATES.map(s => <option key={s.code} value={s.name}>{s.name}</option>)}
                    </select>
                  ) : (
                    <input className="form-control" value={form.shipping_state} onChange={e => f('shipping_state', e.target.value)} disabled={modal.item?.is_active === 0} placeholder="Enter state" />
                  )}
                </div>
              </div>

            </>
          }
        </div>
        <div className="form-row">
          <div className="form-group">
            <label className="form-label">Potential</label>
            <select className="form-control form-select" value={form.potential} onChange={e => f('potential', e.target.value)} disabled={modal.item?.is_active === 0}>
              {POTENTIALS.map(p => <option key={p} value={p} style={{ textTransform: 'capitalize' }}>{p.charAt(0).toUpperCase() + p.slice(1)}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Lifecycle Stage</label>
            <select className="form-control form-select" value={form.lifecycle_stage} onChange={e => f('lifecycle_stage', e.target.value)} disabled={modal.item?.is_active === 0}>
              {['prospect','lead','qualified','opportunity','customer','advocate','churned'].map(s => (
                <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
              <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" /><circle cx="12" cy="7" r="4" /></svg>
              Assign To (MR / Sales Person)
            </label>
            <select className="form-control form-select" value={form.assigned_to} onChange={e => f('assigned_to', e.target.value)} disabled={modal.item?.is_active === 0} style={{ fontWeight: 500 }}>
              <option value="">-- Unassigned / Selection --</option>
              {users
                .filter(u => ['bd', 'sales', 'mr', 'manager', 'admin'].includes(u.role))
                .sort((a, b) => a.name.localeCompare(b.name))
                .map(u => (
                  <option key={u.id} value={u.id}>
                    {u.name} ({u.role === 'mr' ? 'MR' : u.role === 'bd' ? 'BD' : u.role.charAt(0).toUpperCase() + u.role.slice(1)})
                  </option>
                ))
              }
            </select>
          </div>
        </div>
        <div className="form-group">
          <label className="form-label">Territory</label>
          <select className="form-control form-select" value={form.territory_id}
            onChange={e => {
              const tid = e.target.value;
              const tname = territories.find(t => String(t.id) === String(tid))?.name || '';
              setForm(p => ({ ...p, territory_id: tid, territory: tname }));
            }}
            disabled={modal.item?.is_active === 0}>
            <option value="">-- No Territory --</option>
            {territories.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
        </div>
        <div className="form-group"><label className="form-label">Notes</label><textarea className="form-control" rows={2} value={form.notes} onChange={e => f('notes', e.target.value)} disabled={modal.item?.is_active === 0} /></div>
        <div style={{ borderTop: '1px solid var(--border)', paddingTop: 12, marginTop: 4 }}>
          <div style={{ fontWeight: 700, fontSize: '0.82rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: 8 }}>License & Credit</div>
          <div className="form-row">
            <div className="form-group"><label className="form-label">Drug License Number</label><input className="form-control" value={form.license_number} onChange={e => f('license_number', e.target.value)} placeholder="DL-MH-12345" disabled={modal.item?.is_active === 0} /></div>
            <div className="form-group"><label className="form-label">License Expiry Date</label><input className="form-control" type="date" value={form.license_expiry_date} onChange={e => f('license_expiry_date', e.target.value)} disabled={modal.item?.is_active === 0} /></div>
          </div>
          <div className="form-row">
            <div className="form-group"><label className="form-label">Credit Limit (&#8377;)</label><input className="form-control" type="number" value={form.credit_limit} onChange={e => f('credit_limit', e.target.value)} placeholder="e.g. 500000" disabled={modal.item?.is_active === 0} /></div>
          </div>
        </div>
        <div style={{ borderTop: '1px solid var(--border)', paddingTop: 14, marginTop: 4 }}>
          <div style={{ fontWeight: 600, fontSize: '0.85rem', marginBottom: 10, color: 'var(--text-secondary)' }}>Attach Document (optional)</div>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <div className="form-group" style={{ marginBottom: 0, minWidth: 140 }}>
              <label className="form-label" style={{ fontSize: '0.78rem' }}>Document Type</label>
              <select className="form-control form-select" style={{ fontSize: '0.83rem' }} value={createDocForm.doc_type}
                onChange={e => setCreateDocForm(p => ({ ...p, doc_type: e.target.value, doc_description: '' }))}>
                <option value="nda">NDA</option>
                <option value="business_reg">Business Registration</option>
                <option value="gst_cert">GST Certificate</option>
                <option value="drug_license">Drug License</option>
                <option value="pan_card">PAN Card</option>
                <option value="agreement">Agreement / Contract</option>
                <option value="others">Others</option>
              </select>
            </div>
            {createDocForm.doc_type === 'others' && (
              <div className="form-group" style={{ marginBottom: 0, flex: 1, minWidth: 130 }}>
                <label className="form-label" style={{ fontSize: '0.78rem' }}>Describe Type</label>
                <input className="form-control" style={{ fontSize: '0.83rem' }} placeholder="e.g. NDA, Agreement…"
                  value={createDocForm.doc_description} onChange={e => setCreateDocForm(p => ({ ...p, doc_description: e.target.value }))} />
              </div>
            )}
            <div className="form-group" style={{ marginBottom: 0, flex: 2, minWidth: 160 }}>
              <label className="form-label" style={{ fontSize: '0.78rem' }}>File</label>
              <input type="file" className="form-control" style={{ fontSize: '0.83rem' }}
                accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg,.webp,.txt"
                onChange={e => setCreateDocForm(p => ({ ...p, file: e.target.files[0] || null }))} />
            </div>
          </div>
        </div>
      </Modal>

      {/* View Modal */}
      <Modal open={viewModal.open} onClose={() => setViewModal({ open: false })} title="Customer Details" size="lg"
        footer={<button className="btn btn-secondary" onClick={() => setViewModal({ open: false })}>Close</button>}
      >
        {viewModal.customer && (
          <>
            <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start', marginBottom: 18 }}>
              <div style={{ width: 52, height: 52, borderRadius: 12, background: 'var(--primary-lighter)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: '1.2rem', color: 'var(--primary)', flexShrink: 0 }}>
                {viewModal.customer.name.charAt(0)}
              </div>
              <div>
                <div style={{ fontWeight: 700, fontSize: '1.05rem' }}>{viewModal.customer.name}</div>
                <div style={{ display: 'flex', gap: 6, marginTop: 4, flexWrap: 'wrap' }}>
                  <span className={`badge badge-${typeColors[viewModal.customer.type]}`} style={{ textTransform: 'capitalize' }}>{viewModal.customer.type}</span>
                  {viewModal.customer.specialty && <span className="badge badge-neutral">{viewModal.customer.specialty}</span>}
                  <span className={`badge badge-${viewModal.customer.potential === 'high' ? 'success' : viewModal.customer.potential === 'low' ? 'error' : 'warning'}`} style={{ textTransform: 'capitalize' }}>
                    {viewModal.customer.potential} potential
                  </span>
                </div>
              </div>
            </div>
            <div className="detail-grid" style={{ marginBottom: 16 }}>
              <div className="detail-item"><span className="detail-label">Email</span><span className="detail-value">{viewModal.customer.email || '—'}</span></div>
              <div className="detail-item"><span className="detail-label">Phone</span><span className="detail-value">{viewModal.customer.phone || '—'}</span></div>
              <div className="detail-item"><span className="detail-label">Territory</span><span className="detail-value">{viewModal.customer.territory_name || viewModal.customer.territory || '—'}</span></div>
              <div className="detail-item"><span className="detail-label">Assigned To</span><span className="detail-value">{viewModal.customer.assigned_to_name || '—'}</span></div>
              <div className="detail-item"><span className="detail-label">Qualification</span><span className="detail-value">{viewModal.customer.qualification || '—'}</span></div>
              {viewModal.customer.company_id_type && (
                <div className="detail-item"><span className="detail-label">Company ID</span><span className="detail-value"><span style={{ fontWeight: 700, color: 'var(--primary)', marginRight: 4 }}>{viewModal.customer.company_id_type}</span>{viewModal.customer.company_id_value || '—'}</span></div>
              )}
            </div>
            {/* Address sections */}
            {(() => {
              const c = viewModal.customer;
              const primaryAddr = [c.address, c.city, c.state, c.zip_code, c.country].filter(Boolean).join(', ') || '—';
              const billingAddr = c.billing_same_as_primary !== 0
                ? null
                : [c.billing_address, c.billing_city, c.billing_state, c.billing_zip_code, c.billing_country].filter(Boolean).join(', ') || '—';
              const shippingAddr = c.shipping_same_as_primary !== 0
                ? null
                : [c.shipping_address, c.shipping_city, c.shipping_state, c.shipping_zip_code, c.shipping_country].filter(Boolean).join(', ') || '—';
              return (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 10, marginBottom: 16 }}>
                  <div style={{ background: 'var(--surface-2)', borderRadius: 8, padding: '10px 14px', border: '1px solid var(--border)' }}>
                    <div style={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: 4, letterSpacing: '0.4px' }}>Primary Address</div>
                    <div style={{ fontSize: '0.83rem', color: 'var(--text-primary)' }}>{primaryAddr}</div>
                  </div>
                  <div style={{ background: 'var(--surface-2)', borderRadius: 8, padding: '10px 14px', border: '1px solid var(--border)' }}>
                    <div style={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: 4, letterSpacing: '0.4px' }}>Billing Address</div>
                    {billingAddr === null
                      ? <div style={{ fontSize: '0.82rem', color: 'var(--text-muted)', fontStyle: 'italic' }}>Same as Primary</div>
                      : <div style={{ fontSize: '0.83rem', color: 'var(--text-primary)' }}>{billingAddr}</div>
                    }
                  </div>
                  <div style={{ background: 'var(--surface-2)', borderRadius: 8, padding: '10px 14px', border: '1px solid var(--border)' }}>
                    <div style={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: 4, letterSpacing: '0.4px' }}>Shipping Address</div>
                    {shippingAddr === null
                      ? <div style={{ fontSize: '0.82rem', color: 'var(--text-muted)', fontStyle: 'italic' }}>Same as Primary</div>
                      : <div style={{ fontSize: '0.83rem', color: 'var(--text-primary)' }}>{shippingAddr}</div>
                    }
                  </div>
                </div>
              );
            })()}
            {/* Additional Addresses */}
            <div style={{ marginTop: 4, marginBottom: 16, borderTop: '1px solid var(--border)', paddingTop: 14 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                <div style={{ fontWeight: 700, fontSize: '0.88rem' }}>Additional Addresses ({extraAddresses.length})</div>
                {(isAdmin || isManager) && !addrFormOpen && (
                  <button className="btn btn-sm btn-primary" style={{ fontSize: '0.78rem' }} onClick={() => { setAddrEditId(null); setAddrForm({ label: '', address_type: 'other', address: '', city: '', state: '', zip_code: '', country: 'India' }); setAddrFormOpen(true); }}>
                    + Add Address
                  </button>
                )}
              </div>
              {addrFormOpen && (
                <div style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', borderRadius: 8, padding: 14, marginBottom: 12 }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 }}>
                    <div className="form-group" style={{ marginBottom: 0 }}>
                      <label className="form-label" style={{ fontSize: '0.78rem' }}>Label *</label>
                      <input className="form-control" style={{ fontSize: '0.83rem' }} placeholder="e.g. Clinic, Warehouse" value={addrForm.label} onChange={e => setAddrForm(p => ({ ...p, label: e.target.value }))} />
                    </div>
                    <div className="form-group" style={{ marginBottom: 0 }}>
                      <label className="form-label" style={{ fontSize: '0.78rem' }}>Type</label>
                      <select className="form-control form-select" style={{ fontSize: '0.83rem' }} value={addrForm.address_type} onChange={e => setAddrForm(p => ({ ...p, address_type: e.target.value }))}>
                        <option value="delivery">Delivery</option>
                        <option value="branch">Branch</option>
                        <option value="warehouse">Warehouse</option>
                        <option value="registered">Registered</option>
                        <option value="billing">Billing</option>
                        <option value="shipping">Shipping</option>
                        <option value="other">Other</option>
                      </select>
                    </div>
                  </div>
                  <div className="form-group" style={{ marginBottom: 10 }}>
                    <label className="form-label" style={{ fontSize: '0.78rem' }}>Address</label>
                    <textarea className="form-control" rows={2} style={{ fontSize: '0.83rem' }} value={addrForm.address} onChange={e => setAddrForm(p => ({ ...p, address: e.target.value }))} />
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 8, marginBottom: 10 }}>
                    <div className="form-group" style={{ marginBottom: 0 }}>
                      <label className="form-label" style={{ fontSize: '0.78rem' }}>City</label>
                      <input className="form-control" style={{ fontSize: '0.83rem' }} value={addrForm.city} onChange={e => setAddrForm(p => ({ ...p, city: e.target.value }))} />
                    </div>
                    <div className="form-group" style={{ marginBottom: 0 }}>
                      <label className="form-label" style={{ fontSize: '0.78rem' }}>State</label>
                      <input className="form-control" style={{ fontSize: '0.83rem' }} value={addrForm.state} onChange={e => setAddrForm(p => ({ ...p, state: e.target.value }))} />
                    </div>
                    <div className="form-group" style={{ marginBottom: 0 }}>
                      <label className="form-label" style={{ fontSize: '0.78rem' }}>ZIP</label>
                      <input className="form-control" style={{ fontSize: '0.83rem' }} value={addrForm.zip_code} onChange={e => setAddrForm(p => ({ ...p, zip_code: e.target.value }))} />
                    </div>
                    <div className="form-group" style={{ marginBottom: 0 }}>
                      <label className="form-label" style={{ fontSize: '0.78rem' }}>Country</label>
                      <input className="form-control" style={{ fontSize: '0.83rem' }} value={addrForm.country} onChange={e => setAddrForm(p => ({ ...p, country: e.target.value }))} />
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button className="btn btn-primary btn-sm" onClick={handleAddrSave} disabled={addrSaving || !addrForm.label.trim()}>{addrSaving ? 'Saving…' : addrEditId ? 'Update' : 'Add'}</button>
                    <button className="btn btn-secondary btn-sm" onClick={() => { setAddrFormOpen(false); setAddrEditId(null); }}>Cancel</button>
                  </div>
                </div>
              )}
              {extraAddresses.length === 0 && !addrFormOpen ? (
                <div style={{ color: 'var(--text-muted)', fontSize: '0.83rem' }}>No additional addresses added.</div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {extraAddresses.map(addr => (
                    <div key={addr.id} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '10px 12px', background: 'var(--surface-2)', border: '1px solid var(--border)', borderRadius: 8 }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 2 }}>
                          <span style={{ fontWeight: 600, fontSize: '0.85rem' }}>{addr.label}</span>
                          <span style={{ fontSize: '0.7rem', padding: '1px 7px', borderRadius: 8, background: 'var(--primary-lighter)', color: 'var(--primary)', fontWeight: 600, textTransform: 'capitalize' }}>{addr.address_type}</span>
                        </div>
                        <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
                          {[addr.address, addr.city, addr.state, addr.zip_code, addr.country].filter(Boolean).join(', ') || '—'}
                        </div>
                      </div>
                      {(isAdmin || isManager) && (
                        <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
                          <button className="btn btn-ghost btn-icon btn-sm" title="Edit" onClick={() => startAddrEdit(addr)}>
                            <svg width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" /><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" /></svg>
                          </button>
                          <button className="btn btn-ghost btn-icon btn-sm" title="Delete" style={{ color: 'var(--error)' }} onClick={() => handleAddrDelete(addr.id)}>
                            <svg width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14H6L5 6" /><path d="M10 11v6M14 11v6M9 6V4h6v2" /></svg>
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {viewModal.customer.notes && <div style={{ background: 'var(--surface-2)', padding: 12, borderRadius: 8, fontSize: '0.875rem', color: 'var(--text-secondary)', marginBottom: 16 }}>{viewModal.customer.notes}</div>}
            {/* Visits */}
            <div style={{ fontWeight: 700, fontSize: '0.88rem', marginBottom: 8, marginTop: 4 }}>Recent Visits ({viewModal.visits.length})</div>
            {viewModal.visits.length === 0 ? <div style={{ color: 'var(--text-muted)', fontSize: '0.83rem', padding: '6px 0 12px' }}>No visits recorded.</div> : (
              <div style={{ maxHeight: 200, overflow: 'auto', marginBottom: 16 }}>
                {viewModal.visits.map(v => (
                  <div key={v.id} style={{ padding: '10px 0', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div>
                      <div style={{ fontWeight: 500, fontSize: '0.875rem' }}>{v.purpose || 'Visit'}</div>
                      <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{v.user_name}</div>
                    </div>
                    <div style={{ textAlign: 'right', flexShrink: 0 }}>
                      <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>{new Date(v.visit_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</div>
                      <span className={`badge badge-${v.outcome === 'positive' ? 'success' : v.outcome === 'negative' ? 'error' : 'neutral'}`} style={{ textTransform: 'capitalize' }}>{v.outcome}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Orders */}
            <div style={{ borderTop: '1px solid var(--border)', paddingTop: 14, marginTop: 4 }}>
              <div style={{ fontWeight: 700, fontSize: '0.88rem', marginBottom: 8 }}>
                Recent Orders ({viewModal.orders.length})
              </div>
              {viewModal.orders.length === 0 ? (
                <div style={{ color: 'var(--text-muted)', fontSize: '0.83rem', padding: '6px 0 12px' }}>No orders recorded.</div>
              ) : (
                <div style={{ maxHeight: 200, overflow: 'auto', marginBottom: 16 }}>
                  {viewModal.orders.map(order => (
                    <div key={order.id} style={{ padding: '10px 0', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <div>
                        <div style={{ fontWeight: 500, fontSize: '0.875rem' }}>
                          {order.po_number ? `PO: ${order.po_number}` : `Order #${order.id}`}
                        </div>
                        <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                          {order.order_date && new Date(order.order_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                          {order.total_amount && ` · ₹${Number(order.total_amount).toLocaleString()}`}
                          {order.status && ` · ${order.status}`}
                        </div>
                      </div>
                      <div style={{ textAlign: 'right', flexShrink: 0 }}>
                        <span className={`badge badge-${order.status === 'completed' ? 'success' : order.status === 'pending' ? 'warning' : order.status === 'cancelled' ? 'error' : 'neutral'}`} style={{ textTransform: 'capitalize' }}>
                          {order.status || 'Pending'}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Activities */}
            <div style={{ borderTop: '1px solid var(--border)', paddingTop: 14, marginTop: 4 }}>
              <div style={{ fontWeight: 700, fontSize: '0.88rem', marginBottom: 8 }}>
                Upcoming / Pending Activities ({(viewModal.activities || []).filter(a => a.status === 'pending').length})
              </div>
              {(viewModal.activities || []).filter(a => a.status === 'pending').length === 0 ? (
                <div style={{ color: 'var(--text-muted)', fontSize: '0.83rem', padding: '4px 0 8px' }}>No pending activities.</div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 180, overflow: 'auto', marginBottom: 8 }}>
                  {(viewModal.activities || []).filter(a => a.status === 'pending').map(a => (
                    <div key={a.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', background: 'var(--surface-2)', borderRadius: 7, border: '1px solid var(--border)' }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 600, fontSize: '0.83rem', color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.title}</div>
                        <div style={{ fontSize: '0.74rem', color: 'var(--text-muted)', marginTop: 2 }}>
                          {a.type && <span style={{ marginRight: 6, textTransform: 'capitalize' }}>{a.type}</span>}
                          {a.due_date && <span>Due: {new Date(a.due_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</span>}
                          {a.assigned_to_name && <span style={{ marginLeft: 8 }}>· {a.assigned_to_name}</span>}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Competitor Mentions */}
            <div style={{ borderTop: '1px solid var(--border)', paddingTop: 14, marginTop: 4 }}>
              <div style={{ fontWeight: 700, fontSize: '0.88rem', marginBottom: 8 }}>
                Competitor Mentions ({(viewModal.competitorMentions || []).length})
              </div>
              {(viewModal.competitorMentions || []).length === 0 ? (
                <div style={{ color: 'var(--text-muted)', fontSize: '0.83rem', padding: '4px 0 8px' }}>No competitor mentions recorded.</div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 180, overflow: 'auto', marginBottom: 8 }}>
                  {(viewModal.competitorMentions || []).map((m, i) => (
                    <div key={m.id || i} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '8px 12px', background: 'var(--surface-2)', borderRadius: 7, border: '1px solid var(--border)' }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 3 }}>
                          <span style={{ fontWeight: 600, fontSize: '0.83rem', color: 'var(--text-primary)' }}>{m.competitor_name || '—'}</span>
                          <span className={`badge badge-${m.outcome === 'won' ? 'success' : m.outcome === 'lost' ? 'error' : 'neutral'}`} style={{ textTransform: 'capitalize', fontSize: '0.68rem' }}>{m.outcome || 'Neutral'}</span>
                          {m.mention_date && <span style={{ fontSize: '0.73rem', color: 'var(--text-muted)' }}>{new Date(m.mention_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</span>}
                        </div>
                        {m.context && <div style={{ fontSize: '0.77rem', color: 'var(--text-secondary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{m.context}</div>}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Documents */}
            <div style={{ marginTop: 20, borderTop: '1px solid var(--border)', paddingTop: 16 }}>
              <div style={{ fontWeight: 700, fontSize: '0.9rem', marginBottom: 12 }}>Attached Documents</div>
              <div style={{ background: 'var(--surface-2)', borderRadius: 8, padding: 14, marginBottom: 12, border: '1px solid var(--border)' }}>
                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                  <div className="form-group" style={{ marginBottom: 0, minWidth: 140 }}>
                    <label className="form-label" style={{ fontSize: '0.78rem' }}>Document Type</label>
                    <select className="form-control form-select" style={{ fontSize: '0.83rem' }} value={docForm.doc_type}
                      onChange={e => setDocForm(p => ({ ...p, doc_type: e.target.value, doc_description: '' }))}>
                      <option value="nda">NDA</option>
                      <option value="business_reg">Business Registration</option>
                      <option value="gst_cert">GST Certificate</option>
                      <option value="drug_license">Drug License</option>
                      <option value="pan_card">PAN Card</option>
                      <option value="agreement">Agreement / Contract</option>
                      <option value="others">Others</option>
                    </select>
                  </div>
                  {docForm.doc_type === 'others' && (
                    <div className="form-group" style={{ marginBottom: 0, flex: 1, minWidth: 130 }}>
                      <label className="form-label" style={{ fontSize: '0.78rem' }}>Describe Type *</label>
                      <input className="form-control" style={{ fontSize: '0.83rem' }} placeholder="e.g. NDA, Agreement…"
                        value={docForm.doc_description} onChange={e => setDocForm(p => ({ ...p, doc_description: e.target.value }))} />
                    </div>
                  )}
                  <div className="form-group" style={{ marginBottom: 0, flex: 2, minWidth: 160 }}>
                    <label className="form-label" style={{ fontSize: '0.78rem' }}>File</label>
                    <input type="file" className="form-control" style={{ fontSize: '0.83rem' }}
                      accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg,.webp,.txt"
                      onChange={e => setDocForm(p => ({ ...p, file: e.target.files[0] || null }))} />
                  </div>
                  <button className="btn btn-primary btn-sm" onClick={handleDocUpload} disabled={docUploading} style={{ marginBottom: 2 }}>
                    {docUploading ? 'Uploading…' : 'Attach'}
                  </button>
                </div>
                {docError && <div style={{ marginTop: 8, color: 'var(--error)', fontSize: '0.8rem' }}>{docError}</div>}
              </div>
              {docs.length === 0 ? (
                <div style={{ textAlign: 'center', color: 'var(--text-muted)', fontSize: '0.85rem', padding: '8px 0' }}>No documents attached yet.</div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {docs.map(doc => (
                    <div key={doc.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 8 }}>
                      <div style={{ fontSize: '1.1rem' }}>{docTypeIcon(doc.original_name)}</div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 600, fontSize: '0.83rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{doc.original_name}</div>
                        <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>
                          <span style={{ fontWeight: 600, color: 'var(--primary)' }}>
                            {doc.doc_type === 'others' && doc.doc_description ? doc.doc_description : docTypeLabel(doc.doc_type)}
                          </span>
                          {' · '}{doc.uploaded_by_name}
                          {' · '}{new Date(doc.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                        </div>
                      </div>
                      <button className="btn btn-ghost btn-icon btn-sm" onClick={() => handleDocDownload(doc.id, doc.original_name)}>
                        <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" /></svg>
                      </button>
                      <button className="btn btn-ghost btn-icon btn-sm" style={{ color: 'var(--error)' }} onClick={() => handleDocDelete(doc.id)}>
                        <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14H6L5 6" /><path d="M10 11v6M14 11v6M9 6V4h6v2" /></svg>
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </Modal>

      {/* Addresses Modal */}
      <Modal
        open={addrModal.open}
        onClose={() => { setAddrModal({ open: false, entityId: null, entityName: '' }); setAddrFormOpen(false); setAddrEditId(null); }}
        title={`Addresses — ${addrModal.entityName}`}
        size="md"
        footer={<button className="btn btn-secondary" onClick={() => { setAddrModal({ open: false, entityId: null, entityName: '' }); setAddrFormOpen(false); setAddrEditId(null); }}>Close</button>}
      >
        <div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
            <div style={{ fontSize: '0.83rem', color: 'var(--text-muted)' }}>{extraAddresses.length} address{extraAddresses.length !== 1 ? 'es' : ''} saved</div>
            {!addrFormOpen && (
              <button className="btn btn-primary btn-sm" onClick={() => { setAddrEditId(null); setAddrForm({ label: '', address_type: 'other', address: '', city: '', state: '', zip_code: '', country: 'India' }); setAddrFormOpen(true); }}>
                + Add Address
              </button>
            )}
          </div>
          {addrFormOpen && (
            <div style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', borderRadius: 8, padding: 14, marginBottom: 14 }}>
              <div style={{ fontWeight: 600, fontSize: '0.85rem', marginBottom: 10 }}>{addrEditId ? 'Edit Address' : 'New Address'}</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 }}>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label className="form-label">Label *</label>
                  <input className="form-control" placeholder="e.g. Clinic, Dispensary" value={addrForm.label} onChange={e => setAddrForm(p => ({ ...p, label: e.target.value }))} />
                </div>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label className="form-label">Type</label>
                  <select className="form-control form-select" value={addrForm.address_type} onChange={e => setAddrForm(p => ({ ...p, address_type: e.target.value }))}>
                    <option value="delivery">Delivery</option>
                    <option value="branch">Branch</option>
                    <option value="warehouse">Warehouse</option>
                    <option value="registered">Registered</option>
                    <option value="billing">Billing</option>
                    <option value="shipping">Shipping</option>
                    <option value="other">Other</option>
                  </select>
                </div>
              </div>
              <div className="form-group" style={{ marginBottom: 10 }}>
                <label className="form-label">Street Address</label>
                <textarea className="form-control" rows={2} value={addrForm.address} onChange={e => setAddrForm(p => ({ ...p, address: e.target.value }))} />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 8, marginBottom: 12 }}>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label className="form-label">City</label>
                  <input className="form-control" value={addrForm.city} onChange={e => setAddrForm(p => ({ ...p, city: e.target.value }))} />
                </div>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label className="form-label">State</label>
                  <input className="form-control" value={addrForm.state} onChange={e => setAddrForm(p => ({ ...p, state: e.target.value }))} />
                </div>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label className="form-label">ZIP</label>
                  <input className="form-control" value={addrForm.zip_code} onChange={e => setAddrForm(p => ({ ...p, zip_code: e.target.value }))} />
                </div>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label className="form-label">Country</label>
                  <input className="form-control" value={addrForm.country} onChange={e => setAddrForm(p => ({ ...p, country: e.target.value }))} />
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="btn btn-primary btn-sm" onClick={handleAddrSave} disabled={addrSaving || !addrForm.label.trim()}>{addrSaving ? 'Saving…' : addrEditId ? 'Update' : 'Add'}</button>
                <button className="btn btn-secondary btn-sm" onClick={() => { setAddrFormOpen(false); setAddrEditId(null); }}>Cancel</button>
              </div>
            </div>
          )}
          {extraAddresses.length === 0 && !addrFormOpen ? (
            <div style={{ textAlign: 'center', color: 'var(--text-muted)', fontSize: '0.85rem', padding: '24px 0' }}>No additional addresses yet. Click <strong>+ Add Address</strong> to add one.</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {extraAddresses.map(addr => (
                <div key={addr.id} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '12px 14px', background: 'var(--surface-2)', border: '1px solid var(--border)', borderRadius: 8 }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 3 }}>
                      <span style={{ fontWeight: 600, fontSize: '0.87rem' }}>{addr.label}</span>
                      <span style={{ fontSize: '0.7rem', padding: '1px 8px', borderRadius: 8, background: 'var(--primary-lighter)', color: 'var(--primary)', fontWeight: 600, textTransform: 'capitalize' }}>{addr.address_type}</span>
                    </div>
                    <div style={{ fontSize: '0.82rem', color: 'var(--text-secondary)' }}>
                      {[addr.address, addr.city, addr.state, addr.zip_code, addr.country].filter(Boolean).join(', ') || '—'}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
                    <button className="btn btn-ghost btn-icon btn-sm" title="Edit" onClick={() => startAddrEdit(addr)}>
                      <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" /><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" /></svg>
                    </button>
                    <button className="btn btn-ghost btn-icon btn-sm" title="Delete" style={{ color: 'var(--error)' }} onClick={() => handleAddrDelete(addr.id)}>
                      <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14H6L5 6" /><path d="M10 11v6M14 11v6M9 6V4h6v2" /></svg>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </Modal>

      {/* Delete Confirm */}
      <Modal open={!!deleteConfirm} onClose={() => setDeleteConfirm(null)} title="Freeze Customer"
        footer={<>
          <button className="btn btn-secondary" onClick={() => setDeleteConfirm(null)}>Cancel</button>
          <button className="btn btn-danger" onClick={handleDelete}>Freeze</button>
        </>}
      >
        <p>Are you sure you want to freeze <strong>{deleteConfirm?.name}</strong>? The record will be locked from editing. You can unfreeze it later.</p>
      </Modal>

      {/* Bulk Upload */}
      <BulkUpload
        open={bulkOpen}
        onClose={() => setBulkOpen(false)}
        onSuccess={load}
        title="Bulk Upload Customers"
        uploadUrl="/customers/bulk"
        templateUrl="/customers/template"
        columns={[
          { key: 'name', label: 'Name', required: true },
          { key: 'type', label: 'Type', required: true },
          { key: 'specialty', label: 'Specialty' },
          { key: 'qualification', label: 'Qualification' },
          { key: 'email', label: 'Email' },
          { key: 'phone', label: 'Phone' },
          { key: 'company_id_type', label: 'Company ID Type (GSTN/TIN/GST/HST/OTHERS)' },
          { key: 'company_id_value', label: 'Company ID Value' },
          { key: 'address', label: 'Primary Address' },
          { key: 'city', label: 'City (Primary)' },
          { key: 'state', label: 'State (Primary)' },
          { key: 'zip_code', label: 'ZIP / Postal Code' },
          { key: 'country', label: 'Country' },
          { key: 'territory', label: 'Territory' },
          { key: 'potential', label: 'Potential' },
          { key: 'lifecycle_stage', label: 'Lifecycle Stage' },
          { key: 'assigned_to_email', label: 'Assigned To (Email)' },
          { key: 'notes', label: 'Notes' },
          { key: 'billing_same_as_primary', label: 'Billing Same as Primary (1/0)' },
          { key: 'billing_address', label: 'Billing Address' },
          { key: 'billing_city', label: 'Billing City' },
          { key: 'billing_state', label: 'Billing State' },
          { key: 'billing_zip_code', label: 'Billing ZIP' },
          { key: 'billing_country', label: 'Billing Country' },
          { key: 'shipping_same_as_primary', label: 'Shipping Same as Primary (1/0)' },
          { key: 'shipping_address', label: 'Shipping Address' },
          { key: 'shipping_city', label: 'Shipping City' },
          { key: 'shipping_state', label: 'Shipping State' },
          { key: 'shipping_zip_code', label: 'Shipping ZIP' },
          { key: 'shipping_country', label: 'Shipping Country' },
          { key: 'license_number', label: 'License Number' },
          { key: 'license_expiry_date', label: 'License Expiry Date' },
          { key: 'credit_limit', label: 'Credit Limit' },
        ]}
      />
    </div>
  );
}
